import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { apiClientWH } from '@shared/services';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { OrdersApiAdapter } from '../adapters/OrdersApiAdapter';
import { getAvailableActionsByStatus, getStatusForDropdown } from '../config/orderStatusConfig';
import { Order } from '../models/Orders';
import { useOrders } from './useOrders';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  ref: 'ref',
  total: 'total',
  order_created_at: 'order_created_at',
  ready_to_prepare_at: 'ready_to_prepare_at',
  shipment_processed_at: 'processed_at',
  processed_at: 'processed_at',
  last_shipment_update: 'last_shipment_update',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

export const useOrdersMain = () => {
  const { t } = useTranslation('ordersModule');
  const { toast } = useToast();

  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: '' as string },
    sortDirection: { type: 'string', default: '' as string },
    status: { type: 'string', default: '' as string },
  });

  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(() =>
    parseFiltersFromParams(queryParams.filters),
  );
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(filterConditions));
  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      return null;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';
    return {
      field: sortField,
      direction,
    };
  });
  const [statusId, setStatusIdState] = useState<number | undefined>(() => {
    if (!queryParams.status) {
      return undefined;
    }

    const parsed = Number(queryParams.status);
    return Number.isNaN(parsed) ? undefined : parsed;
  });

  // Estado de selección
  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      setSorting((prev) => (prev === null ? prev : null));
      return;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  useEffect(() => {
    const statusValue = queryParams.status;
    const parsed = statusValue ? Number(statusValue) : undefined;
    const normalized = parsed === undefined || Number.isNaN(parsed) ? undefined : parsed;
    setStatusIdState((prev) => (prev === normalized ? prev : normalized));
  }, [queryParams.status]);

  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());
  const [selectedOrdersDataMap, setSelectedOrdersDataMap] = useState<Map<number, Order>>(new Map());

  // Estados de columnas
  const tableColumns = [
    { accessorKey: 'ref', header: t('table.order') },
    { accessorKey: 'products', header: t('table.products') },
    { accessorKey: 'total', header: t('table.total') },
    { accessorKey: 'shop', header: t('table.shop') },
    { accessorKey: 'order_created_at', header: t('table.created_at') },
    { accessorKey: 'ready_to_prepare_at', header: t('table.ready_to_prepare_at') },
    { accessorKey: 'status', header: t('table.status') },
    { accessorKey: 'courier', header: t('table.courier') },
    { accessorKey: 'customer', header: t('table.customer') },
    { accessorKey: 'shipment_processed_at', header: t('table.shipment_date') },
    { accessorKey: 'logistic_status', header: t('table.logistic_status') },
    { accessorKey: 'tracking_number', header: t('table.tracking_number') },
    {
      accessorKey: 'date_tracking_update',
      header: t('table.last_logistic_update'),
    },
    { accessorKey: 'payment_method', header: t('table.payment_method') },
    { accessorKey: 'phone', header: t('table.phone') },
    { accessorKey: 'postal_code', header: t('table.postal_code') },
    // La columna 'team' debe estar presente en la selección de columnas
    // El componente `Table` decide si se renderiza según si existe `row.original.team`
    { accessorKey: 'team', header: t('table.client') },
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.accessorKey));

  // Usar el término de búsqueda directamente como parámetro search
  const searchValue = useMemo(() => searchTerm.trim() || undefined, [searchTerm]);

  // Mapeo de campos de la tabla a parámetros de la API
  // Procesar filtros
  const processedFilters = useMemo(() => {
    try {
      const SPECIAL_COLUMNS = new Set(['event', 'has_order_errors', 'general_error_ids']);
      const regularConditions = filterConditions.filter((c) => !SPECIAL_COLUMNS.has(c.column));

      const searchFilterFromConditions =
        regularConditions.length > 0
          ? {
              filters: regularConditions.map((condition) => {
                const requiresArray = ['in', 'not_in'].includes(condition.action);
                const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

                const value = isNullOperator
                  ? undefined
                  : requiresArray
                    ? Array.isArray(condition.value)
                      ? condition.value
                      : [condition.value]
                    : condition.value;

                return {
                  column: condition.column,
                  operator: condition.action,
                  ...(value !== undefined && { value: value }),
                };
              }),
              logic: 'AND',
            }
          : undefined;

      const event_ids: number[] = [];
      filterConditions.forEach((condition) => {
        if (condition.column === 'event' && condition.action === 'equals') {
          event_ids.push(Number(condition.value));
        }
      });

      const hasOrderErrorsCondition = filterConditions.find((c) => c.column === 'has_order_errors');
      const generalErrorIdsCondition = filterConditions.find((c) => c.column === 'general_error_ids');
      const generalErrorIds = generalErrorIdsCondition?.value
        ? Array.isArray(generalErrorIdsCondition.value)
          ? generalErrorIdsCondition.value
          : [generalErrorIdsCondition.value]
        : undefined;

      const apiField = sorting?.field ? fieldToApiMapping[sorting.field] || 'id' : 'order_created_at';
      const apiDirection = sorting?.direction || 'desc';

      return {
        ...(event_ids.length === 1 && { event_ids: event_ids[0] }),
        ...(event_ids.length > 1 && { event_ids }),
        sortBy: apiField,
        sortDirection: apiDirection,
        ...(searchFilterFromConditions && {
          searchFilter: searchFilterFromConditions,
        }),
        ...(hasOrderErrorsCondition && { has_order_errors: 1 as const }),
        ...(generalErrorIds?.length && { general_error_ids: generalErrorIds }),
      };
    } catch {
      return {};
    }
  }, [filterConditions, sorting]);

  // 🎯 Memoizar el objeto de filtros para evitar re-renders infinitos
  // Cuando el estado es 4 (en preparación), incluir también el estado 5
  const statusIdsForQuery = useMemo(() => {
    if (statusId === undefined) {
      return undefined;
    }
    // Si es el estado 4 (en preparación), incluir también el estado 5
    if (statusId === 4) {
      return [4, 5];
    }
    return statusId;
  }, [statusId]);

  const queryFilters = useMemo(
    () => ({
      search: searchValue,
      ...processedFilters,
      status_ids: statusIdsForQuery,
      sorting,
    }),
    [searchValue, processedFilters, statusIdsForQuery, sorting],
  );

  // Hooks de datos
  const { orders, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, total } =
    useOrders(queryFilters);

  // Detectar si estamos ordenando (isLoading + sorting activo)
  const isSortingActive = useMemo(() => {
    return isFetching && sorting !== null;
  }, [isFetching, sorting]);

  // Usar los datos del Map para los contadores:
  const selectedOrdersData = useMemo(() => Array.from(selectedOrdersDataMap.values()), [selectedOrdersDataMap]);

  // Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setSearchTerm((prev) => (prev === trimmedValue ? prev : trimmedValue));
      updateParam('search', trimmedValue);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((newSelected: string[]) => {
    setSelectedColumns(newSelected);
  }, []);
  const handleStatusChange = useCallback(
    (value?: number) => {
      setStatusIdState((prev) => (prev === value ? prev : value));

      const statusString = value !== undefined ? String(value) : '';
      if ((queryParams.status ?? '') !== statusString) {
        updateParam('status', statusString);
      }
    },
    [queryParams.status, updateParam],
  );

  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);

      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  // Handlers de selección
  const handleToggleOrderSelection = useCallback((order: Order) => {
    setSelectedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(order.id)) {
        newSet.delete(order.id);
        setSelectedOrdersDataMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.delete(order.id);
          return newMap;
        });
      } else {
        newSet.add(order.id);
        setSelectedOrdersDataMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(order.id, order);
          return newMap;
        });
      }

      return newSet;
    });
  }, []);

  const handleSelectAllOrders = useCallback(() => {
    setSelectedOrders((prev) => {
      if (orders.every((order) => prev.has(order.id))) {
        setSelectedOrdersDataMap(new Map());
        return new Set();
      } else {
        setSelectedOrdersDataMap((prevMap) => {
          const newMap = new Map(prevMap);
          orders.forEach((order) => {
            newMap.set(order.id, order);
          });
          return newMap;
        });
        return new Set(orders.map((order) => order.id));
      }
    });
  }, [orders]);

  const handleClearSelection = useCallback(() => {
    setSelectedOrders(new Set());
    setSelectedOrdersDataMap(new Map());
  }, []);

  // 🎯 Handler para cambios de selección desde AG Grid
  const handleSelectionChanged = useCallback(
    (selectedIds: Set<number>) => {
      setSelectedOrders(selectedIds);

      // Actualizar el mapa de datos seleccionados
      setSelectedOrdersDataMap(() => {
        const newMap = new Map();
        selectedIds.forEach((id) => {
          const order = orders.find((o) => o.id === id);
          if (order) {
            newMap.set(id, order);
          }
        });
        return newMap;
      });
    },
    [orders],
  );

  // Funciones auxiliares
  const getStatusForDropdownLocal = useCallback((statusCode: number) => {
    return getStatusForDropdown(statusCode);
  }, []);

  const getAvailableActionsByStatusLocal = useCallback((status: string) => {
    return getAvailableActionsByStatus(status as any);
  }, []);

  // Handlers de acciones
  const handleCancelSelected = useCallback(async (): Promise<boolean> => {
    if (selectedOrders.size === 0) return false;

    try {
      // Filtrar solo los pedidos que se pueden cancelar
      const cancelableOrders = selectedOrdersData.filter((order: any) => {
        const status = getStatusForDropdownLocal(order.status.id);
        const availableActions = getAvailableActionsByStatusLocal(status);
        return availableActions.cancel;
      });

      const orderIds = cancelableOrders.map((order: any) => order.id);

      if (orderIds.length === 0) {
        return false;
      }

      const httpClient = createHttpClient(apiClientWH);
      const apiAdapter = new OrdersApiAdapter(httpClient);

      await apiAdapter.cancelBulkOrders(orderIds);

      // Limpiar selección y refrescar datos
      handleClearSelection();
      refetch();

      // Mostrar toast de éxito
      toast({
        title: t('toast.bulk_cancel_success.title', 'Pedidos cancelados'),
        description: t('toast.bulk_cancel_success.description', {
          count: orderIds.length,
        }),
        variant: 'default',
      });

      return true;
    } catch (error: any) {
      // Mostrar toast de error
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('toast.error.description', 'Ha ocurrido un error al procesar la acción');

      toast({
        title: t('toast.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });

      return false;
    }
  }, [
    selectedOrders,
    selectedOrdersData,
    handleClearSelection,
    refetch,
    toast,
    t,
    getStatusForDropdownLocal,
    getAvailableActionsByStatusLocal,
  ]);

  const handlePrepareSelected = useCallback(
    async (preparationType: 'picking_packing' | 'massive_packing'): Promise<boolean> => {
      if (selectedOrders.size === 0) return false;

      try {
        const httpClient = createHttpClient(apiClientWH);
        const apiAdapter = new OrdersApiAdapter(httpClient);

        const orderIds = Array.from(selectedOrders);
        const preparationTypeNumber = preparationType === 'picking_packing' ? 3 : 1;

        await apiAdapter.markAsPreparation(orderIds, preparationTypeNumber);

        handleClearSelection();
        refetch();

        toast({
          title: t('toast.bulk_preparation_success.title', 'Preparación creada'),
          description: t('toast.bulk_preparation_success.description', {
            count: orderIds.length,
          }),
          variant: 'success',
        });

        return true;
      } catch (error: any) {
        const apiMessage = error?.response?.data?.message;
        toast({
          title: t('toast.error.title', 'Error'),
          description: apiMessage || t('toast.error.description'),
          variant: 'destructive',
        });

        return false;
      }
    },
    [selectedOrders, handleClearSelection, refetch, toast, t],
  );

  const handleMarkForSendSelected = useCallback(async (): Promise<boolean> => {
    if (selectedOrders.size === 0) {
      return false;
    }

    try {
      // Filtrar solo los pedidos que se pueden marcar para enviar
      const sendableOrders = selectedOrdersData.filter((order: any) => {
        const status = getStatusForDropdownLocal(order.status.id);
        const availableActions = getAvailableActionsByStatusLocal(status);
        return availableActions.markForSend;
      });

      const orderIds = sendableOrders.map((order: any) => order.id);

      if (orderIds.length === 0) {
        return false;
      }

      const httpClient = createHttpClient(apiClientWH);
      const apiAdapter = new OrdersApiAdapter(httpClient);

      await apiAdapter.confirmBulkOrders(orderIds);

      // Limpiar selección y refrescar datos
      handleClearSelection();
      refetch();

      // Mostrar toast de éxito
      toast({
        title: t('toast.bulk_confirm_success.title', 'Pedidos confirmados'),
        description: t('toast.bulk_confirm_success.description', {
          count: orderIds.length,
        }),
        variant: 'default',
      });

      return true;
    } catch (error: any) {
      // Mostrar toast de error
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('toast.error.description', 'Ha ocurrido un error al procesar la acción');

      toast({
        title: t('toast.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });

      return false;
    }
  }, [
    selectedOrders,
    selectedOrdersData,
    handleClearSelection,
    refetch,
    toast,
    t,
    getStatusForDropdownLocal,
    getAvailableActionsByStatusLocal,
  ]);

  const handleUncancelSelected = useCallback(async (): Promise<boolean> => {
    if (selectedOrders.size === 0) return false;

    try {
      // Filtrar solo los pedidos que se pueden descancelar
      const uncancelableOrders = selectedOrdersData.filter((order: any) => {
        const status = getStatusForDropdownLocal(order.status.id);
        const availableActions = getAvailableActionsByStatusLocal(status);
        return availableActions.uncancel;
      });

      const orderIds = uncancelableOrders.map((order: any) => order.id);

      if (orderIds.length === 0) {
        return false;
      }

      const httpClient = createHttpClient(apiClientWH);
      const apiAdapter = new OrdersApiAdapter(httpClient);

      await apiAdapter.uncancelBulkOrders(orderIds);

      // Limpiar selección y refrescar datos
      handleClearSelection();
      refetch();

      // Mostrar toast de éxito
      toast({
        title: t('toast.bulk_uncancel_success.title', 'Pedidos descancelados'),
        description: t('toast.bulk_uncancel_success.description', {
          count: orderIds.length,
        }),
        variant: 'default',
      });

      return true;
    } catch (error: any) {
      // Mostrar toast de error
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('toast.error.description', 'Ha ocurrido un error al procesar la acción');

      toast({
        title: t('toast.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });

      return false;
    }
  }, [
    selectedOrders,
    selectedOrdersData,
    handleClearSelection,
    refetch,
    toast,
    t,
    getStatusForDropdownLocal,
    getAvailableActionsByStatusLocal,
  ]);

  const handleCreateLocation = useCallback(() => {
    // TODO: Implementar lógica para crear pedido
  }, []);

  // Calcular contadores
  const actionCounters = useMemo(() => {
    if (selectedOrders.size === 0) {
      return {
        cancelableCount: 0,
        preparableCount: 0,
        sendableCount: 0,
        uncancelableCount: 0,
      };
    }

    const ordersToProcess = selectedOrdersData.length > 0 ? selectedOrdersData : [];

    let cancelableCount = 0;
    let preparableCount = 0;
    let sendableCount = 0;
    let uncancelableCount = 0;

    ordersToProcess.forEach((order: any) => {
      const status = getStatusForDropdownLocal(order.status.id);
      const availableActions = getAvailableActionsByStatusLocal(status);

      if (availableActions.cancel) cancelableCount++;
      if (order.status.id === 3) preparableCount++;
      if (availableActions.markForSend) sendableCount++;
      if (availableActions.uncancel) uncancelableCount++;
    });

    return {
      cancelableCount,
      preparableCount,
      sendableCount,
      uncancelableCount,
    };
  }, [selectedOrders.size, selectedOrdersData, getStatusForDropdownLocal, getAvailableActionsByStatusLocal]);

  // Props para componentes
  const ordersViewProps = useMemo(
    () => ({
      orders,
      isLoading,
      isFetching, // 🎯 Añadir isFetching para control de opacidad
      isSortingActive,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns: [...selectedColumns, 'actions'],
      refetch,
      selectedOrders,
      onToggleOrderSelection: handleToggleOrderSelection,
      onSelectAllOrders: handleSelectAllOrders,
      onClearSelection: handleClearSelection,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [
      orders,
      isLoading,
      isFetching, // 🎯 Añadir isFetching a las dependencias
      isSortingActive,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns,
      refetch,
      selectedOrders,
      handleToggleOrderSelection,
      handleSelectAllOrders,
      handleClearSelection,
      sorting,
      handleSortingChange,
    ],
  );

  // Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return filterConditions.length > 0 || searchValue != null || statusId !== undefined || sorting !== null;
  }, [filterConditions.length, searchValue, statusId, sorting]);
  return {
    // Estados
    filterConditions,
    statusId,
    selectedOrders,
    selectedColumns,
    tableColumns,
    sorting,
    searchTerm,

    // Datos
    orders,
    isLoading,
    isFetching,
    isSortingActive,
    error,
    total,

    // Contadores
    actionCounters,

    // Props para componentes
    ordersViewProps,

    // Handlers
    setStatusId: handleStatusChange,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
    handleCancelSelected,
    handlePrepareSelected,
    handleMarkForSendSelected,
    handleUncancelSelected,
    handleCreateLocation,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    search: searchValue,

    // Filtros procesados para count-by-status (ya excluyen columnas especiales)
    countFilters: {
      searchFilter: processedFilters.searchFilter,
      has_order_errors: processedFilters.has_order_errors,
      general_error_ids: processedFilters.general_error_ids,
    },
  };
};
